import { StickyFooter } from 'src/components'
import { EditOrganization } from 'src/routes/Onboarding/api'
import { useEditOrganization } from 'src/routes/Onboarding/queries'
import { clearStorageCache } from 'src/storage'
import { useGetOrganizationMutation } from '../queries'
import {
  savedChanges,
  storageKey,
  savedLogoutOnLogin,
  savedSplashIos1,
  savedSplashIos2,
  savedSplashAndroid1,
  savedSplashAndroid2,
  savedChatRegion,
  savedAutoReadyLogin,
  savedActivityTimeout,
  savedIAMUserGroup,
  savedIAMAccessKey,
  savedIAMAccessSecret,
  savedIAMPolicyArn,
  savedKvps,
  savedCountryCode,
  savedDebugTimeout,
  savedSfButtonEnabled,
  savedSfButtonKey,
  savedSfLoginUrl,
  savedSfPrincipalArn,
  savedSfRoleArn,
  savedSfClientId,
  savedSfClientSecret,
  savedSfAuthUrl,
  savedSfEndpoint,
  savedSfUsername,
  savedSfPassword,
  savedDisabledAutoMarkdone,
  savedConnectArn,
  savedSamlUrl,
  savedCrmButtonEnabled,
  savedCrmButtonKey,
} from '../storage'
import CustomButton from 'src/components/Shared/UIComponents/Button'

type Props = {
  organization: string
  username: string
}

function getChangedValueByKey(key: string) {
  switch (key) {
    case 'application.autoreadyafterlogin':
      return savedAutoReadyLogin.get()?.toString()
    case 'application.chatregion':
      return savedChatRegion.get()
    case 'application.logoutonlogin':
      return savedLogoutOnLogin.get()?.toString()
    case 'application.kvpsandroidsplash.kvp1':
      return savedSplashAndroid1.get()
    case 'application.kvpsandroidsplash.kvp2':
      return savedSplashAndroid2.get()
    case 'application.kvpsiossplash.kvp1':
      return savedSplashIos1.get()
    case 'application.kvpsiossplash.kvp2':
      return savedSplashIos2.get()
    case 'application.kvps':
      return savedKvps.get()?.reduce((prev, curr, index) => {
        prev[`kvp${index + 1}`] = curr
        return prev
      }, {} as Record<string, string>)
    case 'application.country_code':
      return savedCountryCode.get()
    case 'application.debug_timeout':
      return savedDebugTimeout.get()?.toString()
    case 'activityTimeout':
      return savedActivityTimeout.get()?.toString()
    case 'iam_organization_admin_accessKey':
      return savedIAMAccessKey.get()
    case 'iam_organization_admin_accessSecret':
      return savedIAMAccessSecret.get()
    case 'iam_organization_policy_arn':
      return savedIAMPolicyArn.get()
    case 'iam_organization_usergroup':
      return savedIAMUserGroup.get()
    case 'application.sf_button_enabled':
      return savedSfButtonEnabled.get()
    case 'application.sf_button_key':
      return savedSfButtonKey.get()
    case 'sso.loginUrl':
      return savedSfLoginUrl.get()
    case 'sso.principalArn':
      return savedSfPrincipalArn.get()
    case 'sso.roleArn':
      return savedSfRoleArn.get()
    case 'application.sf_client_id':
      return savedSfClientId.get()
    case 'application.sf_client_secret':
      return savedSfClientSecret.get()
    case 'application.sf_url':
      return savedSfAuthUrl.get()
    case 'application.sf_endpoint':
      return savedSfEndpoint.get()
    case 'application.sf_username':
      return savedSfUsername.get()
    case 'application.sf_password':
      return savedSfPassword.get()
    case 'application.disabled_auto_markdone':
      return savedDisabledAutoMarkdone.get()
    case 'aws_connect_saml_url':
      return savedSamlUrl.get()
    case 'organization_connect_arn':
      return savedConnectArn.get()
    case 'application.crm_button_enabled':
      return savedCrmButtonEnabled.get()
    case 'application.crm_button_key':
      return savedCrmButtonKey.get()
    default:
      return ''
  }
}

export function TrackedSaveButton(props: Props) {
  const [changes] = savedChanges.useState([])
  const getOrganization = useGetOrganizationMutation()
  const editOrganization = useEditOrganization()

  if (!Array.isArray(changes) || changes.length === 0) {
    return <div className="w-full h-6" />
  }

  return (
    <StickyFooter className="justify-center">
      <CustomButton
        label="Reset changes"
        onClick={() => clearStorageCache(storageKey)}
        disabled={getOrganization.isLoading || editOrganization.isLoading}
      />

      <CustomButton
        label="Save changes"
        onClick={async () => {
          const orgChanges = changes.reduce((acc, key) => {
            let [rootKey, nestedKey, kvpKey] = key.split('.')
            const value = getChangedValueByKey(key)

            if (rootKey && nestedKey) {
              // @ts-ignore
              if (!acc[rootKey]) acc[rootKey] = {}
              // @ts-ignore
              if (kvpKey && !acc[rootKey][nestedKey])
                // @ts-ignore
                acc[rootKey][nestedKey] = {}
            }

            // @ts-ignore
            if (nestedKey && kvpKey) acc[rootKey][nestedKey][kvpKey] = value
            // @ts-ignore
            else if (nestedKey) acc[rootKey][nestedKey] = value
            // @ts-ignore
            else acc[rootKey] = value

            return acc
          }, {} as EditOrganization)

          if (
            // when toggling on crm_button_enabled make sure crm_button_key is saved in DB as well
            orgChanges.application?.crm_button_enabled &&
            !orgChanges.application?.crm_button_key
          ) {
            orgChanges.application.crm_button_key =
              savedCrmButtonKey.get() ?? 'CRM_URL'
          }

          await editOrganization.mutateAsync({
            ...orgChanges,
            organization: props.organization,
            username: props.username,
          })

          savedChanges.clear()
          savedIAMAccessSecret.clear()
        }}
        isLoading={getOrganization.isLoading || editOrganization.isLoading}
        isPrimary
        className="ml-8 bg-accent-foreground text-white hover:text-white hover:bg-theme-600"
      />
    </StickyFooter>
  )
}
